import React, { ReactNode, useEffect, useState } from 'react';
import { LicenseInfo } from '@mui/x-license';
import 'src/global.css';

// ----------------------------------------------------------------------

import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { decodeJWT } from 'aws-amplify/auth';

import { PrivateRouter, PublicRouter } from './routes/sections';
import { SplashScreen } from './components/loading-screen';
import AuthUtils from './utils/AuthUtils';
import Services from './services/Services';
import { useAuthContext } from './context/AuthContext';
import { AppContextProvider, useAppContext } from './context/AppContext';
import { environment } from './environment/environment';
import { decrypt } from './utils/LocalStorage';
import BannerDetailsProvider from './context/BannerContext';

import { useScrollToTop } from 'src/hooks/use-scroll-to-top';
import { CONFIG } from 'src/config-global';
import { LocalizationProvider } from 'src/locales';
import { I18nProvider } from 'src/locales/i18n-provider';
import { ThemeProvider } from 'src/theme/theme-provider';
import { Snackbar } from 'src/components/snackbar';
import { ProgressBar } from 'src/components/progress-bar';
import { MotionLazy } from 'src/components/animate/motion-lazy';
import { SettingsDrawer, defaultSettings, SettingsProvider } from 'src/components/settings';
import { ConfigProvider } from 'src/context/ConfigContext';
import { AuthProvider as JwtAuthProvider } from 'src/auth/context/jwt';
import { AuthProvider as Auth0AuthProvider } from 'src/auth/context/auth0';
import { AuthProvider as AmplifyAuthProvider } from 'src/auth/context/amplify';
import { AuthProvider as SupabaseAuthProvider } from 'src/auth/context/supabase';
import { AuthProvider as FirebaseAuthProvider } from 'src/auth/context/firebase';
import AutoLogout from 'src/components/auto-logout/AutoLogout';

// ----------------------------------------------------------------------

const AuthProvider =
  (CONFIG.auth.method === 'amplify' && AmplifyAuthProvider) ||
  (CONFIG.auth.method === 'firebase' && FirebaseAuthProvider) ||
  (CONFIG.auth.method === 'supabase' && SupabaseAuthProvider) ||
  (CONFIG.auth.method === 'auth0' && Auth0AuthProvider) ||
  JwtAuthProvider;

export default function App() {
  useScrollToTop();
  const { authState, login } = useAuthContext();
  const { appState } = useAppContext() ?? {};
  const { isAuthenticated } = authState;
  const authConfig = appState?.authConfig ?? null;

  const [msalAuthConfig, setMsalAuthConfig] = useState<any>(null);
  const [isAppReady, setAppReady] = useState(false);

  const attachAppListeners = (): void => {
    // Any app listeners to be added
  };
  const removeAppListeners = (): void => {
    // App app listeners to be removed
  };

  useEffect(() => {
    async function onMount() {
      // Need to remove this line, temporary
      const token = AuthUtils.getAuthToken();
      if (token?.length) {
        login({ token });
        Services.initAfterAuth({ token });
        const decodedToken = decodeJWT(token);
      }
      setTimeout(() => {
        setAppReady(true);
      }, 100);
      attachAppListeners();
    }
    onMount();
    return () => {
      removeAppListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderRoutes = (): ReactNode => {
    if (!isAppReady) {
      return <SplashScreen />;
    }
    // Render the private routes
    if (isAuthenticated) {
      return (
        <AutoLogout>
          <PrivateRouter />
        </AutoLogout>
      );
    }
    return <PublicRouter />;
  };

  useEffect(() => {
    const authTempObj = decrypt(localStorage.getItem('authConfig'));
    const authConfigObj: any = authTempObj ? JSON.parse(authTempObj) : null;
    setMsalAuthConfig(authConfigObj || environment?.msalConfig?.auth?.[0]);
  }, [authConfig]);

  const msalInstance = new PublicClientApplication({
    ...environment.msalConfig,
    auth: msalAuthConfig,
  });

  useEffect(() => {
    LicenseInfo.setLicenseKey(environment.muiLicenseKey);
  }, []);

  return (
    <MsalProvider instance={msalInstance}>
      <AppContextProvider>
        <I18nProvider>
          <SettingsProvider settings={defaultSettings}>
            <ThemeProvider>
              <ConfigProvider>
                <LocalizationProvider>
                  <AuthProvider>
                    <BannerDetailsProvider>
                      <MotionLazy>
                        <Snackbar />
                        <ProgressBar />
                        <SettingsDrawer />
                        {renderRoutes()}
                      </MotionLazy>
                    </BannerDetailsProvider>
                  </AuthProvider>
                </LocalizationProvider>
              </ConfigProvider>
            </ThemeProvider>
          </SettingsProvider>
        </I18nProvider>
      </AppContextProvider>
    </MsalProvider>
  );
}
